import React, { useEffect, useMemo, useState } from "react";
import {
  createClientForm,
  getClientForm,
  updateClientForm,
  listScenarios,
  listCreditors,
} from "../../api";
import { getCallDebtEntries } from "../../utils/callDebts";
import {
  cleanClientHistoryForPayload,
  getClientHistoryEntriesForForm,
} from "../../utils/clientHistory";

const LEGACY_DEBT_TYPES = ["Бяла Карта", "Easy Credit", "Viva Credit", "ДСК", "ОББ", "УниКредит", "ПИБ", "Аксес Файнанс", "Креди Йес", "Микро Кредит"];

const BTN =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-[#1f2638] bg-[#121825] px-4 py-2 text-sm font-semibold hover:bg-[#141b2d] focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_PRIMARY =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-violet-600/40 bg-violet-600/20 px-4 py-2 text-sm font-semibold hover:bg-violet-600/30 focus:outline-none focus:ring-2 focus:ring-violet-600/60 disabled:opacity-60 disabled:cursor-not-allowed";
const BTN_GHOST =
  "inline-flex items-center justify-center gap-2 rounded-xl border border-transparent bg-transparent px-3 py-2 text-sm font-semibold hover:bg-[#0e1320] focus:outline-none focus:ring-2 focus:ring-violet-600/40 disabled:opacity-60 disabled:cursor-not-allowed";

const INP =
  "w-full bg-[#121825] border border-[#1f2638] rounded-xl px-3 py-2 text-sm outline-none focus:ring-2 focus:ring-violet-600/40";
const LBL = "text-xs text-slate-400";
const H2 = "text-xs uppercase tracking-wider text-slate-300";
const CARD = "rounded-2xl border border-[#1f2638] bg-[#0e1320]/50 p-5";
const REGULAR_PAYER_SCENARIO_ID = "695f76a63de1300129b316c3";
const REGULAR_PAYER_SCENARIO_NAME = "Редовен платец";
const REGULAR_PAYER_SCENARIO_ID_NORM = REGULAR_PAYER_SCENARIO_ID.toLowerCase();
const REGULAR_PAYER_SCENARIO_NAME_NORM = REGULAR_PAYER_SCENARIO_NAME.toLowerCase();
const BROKEN_PROMISE_SCENARIO_NAME = "Неспазено обещание";
const BROKEN_PROMISE_SCENARIO_NAME_NORM = BROKEN_PROMISE_SCENARIO_NAME.toLowerCase();
const THIRD_PERSON_SCENARIO_NAME = "Трето лице";
const THIRD_PERSON_SCENARIO_NAME_NORM = THIRD_PERSON_SCENARIO_NAME.toLowerCase();
const mkKey = () =>
  (typeof crypto !== "undefined" && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}_${Math.random().toString(16).slice(2)}`;

function uniqCreditorNames(values = []) {
  const out = [];
  const seen = new Set();

  for (const value of values) {
    const name = String(value || "").trim();
    if (!name) continue;
    const key = name.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(name);
  }

  return out;
}

function getPreferredCreditorName(values = []) {
  return uniqCreditorNames(values)[0] || "";
}

function createDebtEntry(defaultCreditor = "", forCall = true) {
  return {
    _key: mkKey(),
    creditor: String(defaultCreditor || "").trim(),
    amount: "",
    forCall: !!forCall,
    paid: false,
    installment: "",
    dueDate: "",
  };
}

function createHistoryEntry(lastCallDate = "", lastCallResult = "") {
  return {
    _key: mkKey(),
    lastCallDate,
    lastCallResult,
  };
}

function createHistoryEntriesForForm(history) {
  return getClientHistoryEntriesForForm(history).map((entry) =>
    createHistoryEntry(entry.lastCallDate, entry.lastCallResult)
  );
}

function normalizeScenarioToken(v) {
  return String(v || "").trim().toLowerCase();
}

function normalizeDateInput(v) {
  const s = String(v || "").trim();
  if (!s) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return "";

  const [y, m, d] = s.split("-").map(Number);
  if (!Number.isInteger(y) || !Number.isInteger(m) || !Number.isInteger(d)) return "";

  const dt = new Date(Date.UTC(y, m - 1, d));
  if (
    dt.getUTCFullYear() !== y ||
    dt.getUTCMonth() + 1 !== m ||
    dt.getUTCDate() !== d
  ) {
    return "";
  }
  return s;
}

function isRegularPayerScenarioSelected(scenarioValue, scenarios = []) {
  const currentNorm = normalizeScenarioToken(scenarioValue);
  if (!currentNorm) return false;

  if (currentNorm === REGULAR_PAYER_SCENARIO_ID_NORM || currentNorm === REGULAR_PAYER_SCENARIO_NAME_NORM) {
    return true;
  }

  const byId = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.id) === currentNorm
  );
  if (normalizeScenarioToken(byId?.name) === REGULAR_PAYER_SCENARIO_NAME_NORM) return true;

  const byName = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.name) === currentNorm
  );
  return normalizeScenarioToken(byName?.id) === REGULAR_PAYER_SCENARIO_ID_NORM;
}

function isBrokenPromiseScenarioSelected(scenarioValue, scenarios = []) {
  const currentNorm = normalizeScenarioToken(scenarioValue);
  if (!currentNorm) return false;

  if (currentNorm === BROKEN_PROMISE_SCENARIO_NAME_NORM) {
    return true;
  }

  const byId = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.id) === currentNorm
  );
  const byIdNameNorm = normalizeScenarioToken(byId?.name);
  if (byIdNameNorm === BROKEN_PROMISE_SCENARIO_NAME_NORM) {
    return true;
  }

  const byName = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.name) === currentNorm
  );
  const byNameNorm = normalizeScenarioToken(byName?.name);
  return byNameNorm === BROKEN_PROMISE_SCENARIO_NAME_NORM;
}

function isThirdPersonScenarioSelected(scenarioValue, scenarios = []) {
  const currentNorm = normalizeScenarioToken(scenarioValue);
  if (!currentNorm) return false;

  if (currentNorm === THIRD_PERSON_SCENARIO_NAME_NORM) return true;

  const byId = (Array.isArray(scenarios) ? scenarios : []).find(
    (s) => normalizeScenarioToken(s?.id) === currentNorm
  );
  return normalizeScenarioToken(byId?.name) === THIRD_PERSON_SCENARIO_NAME_NORM;
}

function moneyToFormValue(v) {
  if (v === null || v === undefined) return "";
  return formatMoneyOnBlur(String(v));
}


function normalizeDebtsFromClient(p) {
  const paidSrc =
    Array.isArray(p?.paidDebts) ? p.paidDebts :
      Array.isArray(p?.paid_debts) ? p.paid_debts :
        null;

  const rawDebts =
    Array.isArray(p?.debts) && p.debts.length
      ? p.debts
      : (p?.debt ? [p.debt] : []);

  if (!rawDebts.length) {
    return {
      debts: [createDebtEntry("", true)],
    };
  }

  const callDebtIndexSet = new Set(
    getCallDebtEntries(p, rawDebts).map((entry) => entry.index)
  );

  return {
    debts: rawDebts.map((d, i) => ({
      _key: mkKey(),
      creditor: String(d?.creditor || "").trim(),
      amount: moneyToFormValue(d?.amount),
      forCall: callDebtIndexSet.has(i),
      installment:
        d?.installment === null || d?.installment === undefined
          ? moneyToFormValue(d?.installment_amount)
          : moneyToFormValue(d?.installment),
      dueDate: normalizeDateInput(d?.dueDate ?? d?.due_date),
      paid: Boolean(
        d?.paid ??
        paidSrc?.[i]?.paid ??
        paidSrc?.[i]?.is_paid ??
        paidSrc?.[i]?.isPaid ??
        false
      ),
    })),
  };
}

function cleanDebtsForPayload(debts) {
  const cleaned = (Array.isArray(debts) ? debts : [])
    .map(d => {
      const amount = toMoneyApiStringOrNull(d?.amount);
      const installment = toMoneyApiStringOrNull(d?.installment);

      return {
        creditor: (d?.creditor || "").trim(),
        amount,
        forCall: !!d?.forCall,
        installment,
        installment_amount: toMoneyOrNull(d?.installment),
        dueDate: normalizeDateInput(d?.dueDate),
      };
    })
    .filter(d => d.creditor || d.amount !== null);

  return cleaned.length
    ? cleaned
    : [{ creditor: "", amount: null, forCall: true, installment: null, installment_amount: null, dueDate: "" }];
}

function cleanPaidDebtsForPayload(debts) {
  const cleaned = (Array.isArray(debts) ? debts : [])
    .map(d => ({
      creditor: (d?.creditor || "").trim(),
      amount: toMoneyApiStringOrNull(d?.amount),
      paid: !!d?.paid,
    }))
    .filter(d => d.creditor || d.amount !== null || d.paid);

  return cleaned.length ? cleaned : [{ creditor: "", amount: null, paid: false }];
}

function onlyDigits(v) {
  return (v || "").replace(/[^0-9]/g, "");
}
function toIntOrNull(v) {
  const s = onlyDigits(v);
  if (!s) return null;
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

function normalizeMoneyInput(raw) {
  let s = String(raw ?? "");
  s = s.replace(/\./g, ",");
  s = s.replace(/[^\d,]/g, "");
  if (s.startsWith(",")) s = "0" + s;

  const firstComma = s.indexOf(",");
  if (firstComma >= 0) {
    const intPart = s.slice(0, firstComma).replace(/^0+(?=\d)/, "");
    const rest = s.slice(firstComma + 1).replace(/,/g, "");
    const decPart = rest.slice(0, 2);
    return `${intPart || "0"},${decPart}`;
  }

  return s.replace(/^0+(?=\d)/, "");
}

function formatMoneyOnBlur(v) {
  const s = String(v ?? "").trim();
  if (!s) return "";

  const norm = normalizeMoneyInput(s);
  const idx = norm.indexOf(",");
  if (idx < 0) return norm;

  const intPart = norm.slice(0, idx);
  const decPart = norm.slice(idx + 1);

  if (decPart.length === 0) return intPart;
  if (decPart.length === 1) return `${intPart},${decPart}0`;
  return `${intPart},${decPart}`;
}

function toMoneyApiStringOrNull(v) {
  const s = formatMoneyOnBlur(v);
  if (!s) return null;

  const norm = s.replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(norm)) return null;

  return norm;
}

function toMoneyOrNull(v) {
  const s = String(v ?? "").trim();
  if (!s) return null;

  const norm = s.replace(",", ".");
  if (!/^\d+(\.\d{0,2})?$/.test(norm)) return null;

  const n = Number(norm);
  if (!Number.isFinite(n)) return null;

  return Math.round(n * 100) / 100;
}

function errToText(e) {
  if (!e) return "Грешка";
  if (typeof e === "string") return e;

  if (e instanceof Error) {
    return e.message || "Грешка";
  }

  const m = e?.message;
  if (typeof m === "string") return m;
  if (m && typeof m === "object") {
    try {
      return JSON.stringify(m);
    } catch { }
  }

  if (typeof e?.detail === "string") return e.detail;

  try {
    return JSON.stringify(e);
  } catch {
    return String(e);
  }
}

export default function FormAdmin({
  onBack,
  clientId = null,
  onSaved,
}) {
  const [toast, setToast] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [egn, setEgn] = useState("");

  const [phone, setPhone] = useState("");
  const [thirdPerson, setThirdPerson] = useState("no");
  const [thirdPersonPhone, setThirdPersonPhone] = useState("");
  const [thirdPersonFirstName, setThirdPersonFirstName] = useState("");
  const [thirdPersonLastName, setThirdPersonLastName] = useState("");

  const [debts, setDebts] = useState(() => [createDebtEntry("", true)]);


  const [historyEntries, setHistoryEntries] = useState(() => [createHistoryEntry()]);

  const [isWorking, setIsWorking] = useState("no");
  const [salary, setSalary] = useState("");

  const [notes, setNotes] = useState("");

  const [scenario, setScenario] = useState("");
  const [scenarios, setScenarios] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);
  const [scenariosErr, setScenariosErr] = useState("");
  const [creditorItems, setCreditorItems] = useState([]);
  const [loadingCreditors, setLoadingCreditors] = useState(false);
  const [creditorsErr, setCreditorsErr] = useState("");
  const [creditorsLoaded, setCreditorsLoaded] = useState(false);
  const isRegularPayerScenario = useMemo(
    () => isRegularPayerScenarioSelected(scenario, scenarios),
    [scenario, scenarios]
  );
  const isBrokenPromiseScenario = useMemo(
    () => isBrokenPromiseScenarioSelected(scenario, scenarios),
    [scenario, scenarios]
  );
  const showInstallmentField = isRegularPayerScenario || isBrokenPromiseScenario;
  const showDueDateField = isBrokenPromiseScenario;
  const isThirdPersonScenario = useMemo(
    () => isThirdPersonScenarioSelected(scenario, scenarios),
    [scenario, scenarios]
  );
  const effectiveThirdPerson = isThirdPersonScenario ? "yes" : thirdPerson;

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setScenariosErr("");
        setLoadingScenarios(true);
        const arr = await listScenarios();
        if (!alive) return;
        setScenarios(arr || []);
      } catch (e) {
        if (!alive) return;
        setScenarios([]);
        setScenariosErr(e?.message || "Грешка при зареждане на сценарии");
      } finally {
        if (alive) setLoadingScenarios(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        setCreditorsErr("");
        setLoadingCreditors(true);
        const arr = await listCreditors();
        if (!alive) return;
        setCreditorItems(arr || []);
      } catch (e) {
        if (!alive) return;
        setCreditorItems([]);
        setCreditorsErr(e?.message || "Грешка при зареждане на кредитори");
      } finally {
        if (!alive) return;
        setLoadingCreditors(false);
        setCreditorsLoaded(true);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 2200);
    return () => clearTimeout(t);
  }, [toast]);

  useEffect(() => {
    if (thirdPerson !== "yes") {
      setThirdPersonPhone("");
      setThirdPersonFirstName("");
      setThirdPersonLastName("");
    }
  }, [thirdPerson]);

  useEffect(() => {
    if (!isThirdPersonScenario) return;
    if (thirdPerson !== "yes") {
      setThirdPerson("yes");
    }
  }, [isThirdPersonScenario, thirdPerson]);


  useEffect(() => {
    if (isWorking !== "yes") setSalary("");
  }, [isWorking]);

  useEffect(() => {
    let alive = true;

    async function run() {
      if (!clientId) return;

      try {
        setLoading(true);

        const data = await getClientForm(clientId);

        const p = data || {};

        if (!alive) return;

        setFirstName(p.personal?.firstName || "");
        setLastName(p.personal?.lastName || "");
        setEgn(p.personal?.egn || "");

        setPhone(p.contacts?.phone || "");
        setThirdPerson(p.contacts?.thirdPerson || "no");
        setThirdPersonPhone(p.contacts?.thirdPersonPhone || "");
        setThirdPersonFirstName(p.contacts?.thirdPersonFirstName || "");
        setThirdPersonLastName(p.contacts?.thirdPersonLastName || "");


        const norm = normalizeDebtsFromClient(p);
        setDebts(norm.debts);


        setHistoryEntries(createHistoryEntriesForForm(p.history));

        setIsWorking(p.work?.isWorking || "no");
        setSalary(
          p.work?.salary === null || p.work?.salary === undefined ? "" : String(p.work.salary)
        );

        setScenario(p.scenario || "");

        setNotes(p.notes || "");
      } catch (e) {
        setToast(errToText(e));
      } finally {
        if (alive) setLoading(false);
      }
    }

    run();
    return () => {
      alive = false;
    };
  }, [clientId]);

  useEffect(() => {
    if (!scenarios.length) return;

    const currentNorm = normalizeScenarioToken(scenario);
    if (!currentNorm) {
      setScenario(scenarios[0]?.id || "");
      return;
    }

    const hasId = scenarios.some((s) => normalizeScenarioToken(s?.id) === currentNorm);
    if (hasId) return;

    const byName = scenarios.find((s) => normalizeScenarioToken(s?.name) === currentNorm);
    if (byName?.id) {
      setScenario(byName.id);
      return;
    }

    setScenario(scenarios[0]?.id || "");
  }, [scenarios, scenario]);

  useEffect(() => {
    setDebts((prev) => {
      let changed = false;
      const next = prev.map((d) => {
        const installment = String(d?.installment || "").trim();
        const dueDate = String(d?.dueDate || "").trim();

        if (!showInstallmentField) {
          if (!installment && !dueDate && d?.installment !== undefined && d?.dueDate !== undefined) {
            return d;
          }
          changed = true;
          return { ...d, installment: "", dueDate: "" };
        }

        if (!showDueDateField && dueDate) {
          changed = true;
          return { ...d, dueDate: "" };
        }

        return d;
      });
      return changed ? next : prev;
    });
  }, [showInstallmentField, showDueDateField]);

  const creditorNamesFromApi = useMemo(
    () => uniqCreditorNames((creditorItems || []).map((item) => item?.name)),
    [creditorItems]
  );

  const debtCreditorNames = useMemo(
    () => uniqCreditorNames((debts || []).map((item) => item?.creditor)),
    [debts]
  );

  const baseCreditorNames = useMemo(() => {
    if (creditorNamesFromApi.length) return creditorNamesFromApi;
    if (creditorsLoaded) return LEGACY_DEBT_TYPES;
    return [];
  }, [creditorNamesFromApi, creditorsLoaded]);

  const creditorOptions = useMemo(() => {
    return uniqCreditorNames([...baseCreditorNames, ...debtCreditorNames]);
  }, [baseCreditorNames, debtCreditorNames]);

  const defaultCreditorName = useMemo(
    () => getPreferredCreditorName(baseCreditorNames),
    [baseCreditorNames]
  );

  useEffect(() => {
    if (!defaultCreditorName) return;

    setDebts((prev) => {
      let changed = false;
      const next = prev.map((debt) => {
        if (String(debt?.creditor || "").trim()) return debt;
        changed = true;
        return { ...debt, creditor: defaultCreditorName };
      });
      return changed ? next : prev;
    });
  }, [defaultCreditorName]);

  const innerPayload = useMemo(() => {
    const cleanedDebts = cleanDebtsForPayload(debts).map((debt) => {
      if (!showInstallmentField) {
        if (debt.installment === null && debt.installment_amount === null && !debt.dueDate) {
          return debt;
        }
        return { ...debt, installment: null, installment_amount: null, dueDate: "" };
      }

      if (!showDueDateField) {
        if (!debt.dueDate) return debt;
        return { ...debt, dueDate: "" };
      }

      return debt;
    });
    const callDebts = cleanedDebts.filter((debt) => debt.forCall);
    const primaryDebt =
      callDebts[0] ||
      cleanedDebts[0] ||
      { creditor: "", amount: null, forCall: true, installment: null, dueDate: "" };

    return {
      scenario: scenario || "",
      personal: {
        firstName: (firstName || "").trim(),
        lastName: (lastName || "").trim(),
        egn: (egn || "").trim(),
      },
      contacts: {
        phone: (phone || "").trim(),
        thirdPerson: effectiveThirdPerson,
        thirdPersonPhone: effectiveThirdPerson === "yes" ? (thirdPersonPhone || "").trim() : "",
        thirdPersonFirstName: effectiveThirdPerson === "yes" ? (thirdPersonFirstName || "").trim() : "",
        thirdPersonLastName: effectiveThirdPerson === "yes" ? (thirdPersonLastName || "").trim() : "",
      },


      debts: cleanedDebts,
      callDebts,
      debt: primaryDebt,
      paidDebts: cleanPaidDebtsForPayload(debts),

      history: cleanClientHistoryForPayload(historyEntries),
      work: {
        isWorking,
        salary: isWorking === "yes" ? toIntOrNull(salary) : null,
      },
      notes: notes || "",
    };
  }, [
    scenario,
    firstName,
    lastName,
    egn,
    phone,
    effectiveThirdPerson,
    thirdPersonPhone,
    thirdPersonFirstName,
    thirdPersonLastName,
    debts,
    historyEntries,
    isWorking,
    salary,
    notes,
    showInstallmentField,
    showDueDateField,
  ]);


  const validate = () => {
    if (!innerPayload.personal.firstName || !innerPayload.personal.lastName) {
      return "Попълни име и фамилия.";
    }
    if (innerPayload.personal.egn && innerPayload.personal.egn.length !== 10) {
      return "ЕГН трябва да е 10 цифри.";
    }
    if (!innerPayload.contacts.phone) {
      return "Попълни телефон.";
    }
    if (innerPayload.contacts.thirdPerson === "yes") {
      if (!innerPayload.contacts.thirdPersonPhone) {
        return "Попълни телефон на трето лице.";
      }
      if (!innerPayload.contacts.thirdPersonFirstName || !innerPayload.contacts.thirdPersonLastName) {
        return "Попълни име и фамилия на трето лице.";
      }
    }

    if (!Array.isArray(innerPayload.debts) || innerPayload.debts.length === 0) {
      return "Добави поне едно задължение.";
    }
    const bad = innerPayload.debts.find(d => !d.creditor || d.amount === null);
    if (bad) {
      return "Всяко задължение трябва да има кредитор и сума.";
    }
    if (!Array.isArray(innerPayload.callDebts) || innerPayload.callDebts.length === 0) {
      return "Маркирай поне едно задължение за обаждане.";
    }
    if (showInstallmentField) {
      const noInstallment = innerPayload.debts.find((d) => d.installment === null);
      if (noInstallment) {
        if (isBrokenPromiseScenario) {
          return "При сценарий „Неспазено обещание“ всяко задължение трябва да има вноска.";
        }
        return "При сценарий „Редовен платец“ всяко задължение трябва да има вноска.";
      }
    }
    if (showDueDateField) {
      const noDueDate = innerPayload.debts.find((d) => !d.dueDate);
      if (noDueDate) {
        return "При сценарий „Неспазено обещание“ всяко задължение трябва да има падежна дата.";
      }
    }

    return null;
  };


  const save = async () => {
    const msg = validate();
    if (msg) {
      setToast(msg);
      return;
    }

    try {
      setSaving(true);

      const apiBody = innerPayload;

      let res;
      if (clientId) {
        res = await updateClientForm(clientId, apiBody);
      } else {
        res = await createClientForm(apiBody);
      }

      setToast("Запазено");
      if (typeof onSaved === "function") onSaved(res);
    } catch (e) {
      setToast(errToText(e));
    } finally {
      setSaving(false);
    }
  };

  const reset = () => {
    setFirstName("");
    setLastName("");
    setEgn("");
    setPhone("");
    setThirdPerson("no");
    setThirdPersonPhone("");
    setThirdPersonFirstName("");
    setThirdPersonLastName("");
    setDebts([createDebtEntry(defaultCreditorName, true)]);
    setHistoryEntries([createHistoryEntry()]);
    setIsWorking("no");
    setSalary("");
    setNotes("");
    setScenario(scenarios[0]?.id || "");
    setToast("Изчистено");
  };


  const disabled = saving || loading;

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-xl font-bold">{clientId ? "Редакция" : "Форма"}</h1>
        <div className="flex-1" />

        {typeof onBack === "function" && (
          <button className={BTN_GHOST} onClick={onBack} disabled={saving}>
            Назад
          </button>
        )}

        <button className={BTN} onClick={reset} disabled={disabled}>
          Изчисти
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <section className={CARD}>
          <div className={H2}>ЛИЧНИ ДАННИ</div>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="sm:col-span-2">
              <div className={LBL}>Сценарий</div>
              <select
                className={INP}
                value={scenario}
                onChange={(e) => setScenario(e.target.value)}
                disabled={disabled || loadingScenarios}
              >
                {!scenarios.length ? (
                  <option value="">
                    {loadingScenarios ? "Зареждане..." : "Няма сценарии"}
                  </option>
                ) : (
                  scenarios.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.name || s.id}
                    </option>
                  ))
                )}
              </select>
              {!!scenariosErr && (
                <div className="mt-2 text-xs text-rose-300">
                  {scenariosErr}
                </div>
              )}
            </div>
            <div>
              <div className={LBL}>Име</div>
              <input className={INP} value={firstName} onChange={(e) => setFirstName(e.target.value)} disabled={disabled} />
            </div>
            <div>
              <div className={LBL}>Фамилия</div>
              <input className={INP} value={lastName} onChange={(e) => setLastName(e.target.value)} disabled={disabled} />
            </div>
            <div className="sm:col-span-2">
              <div className={LBL}>ЕГН</div>
              <input
                className={INP}
                value={egn}
                onChange={(e) => setEgn(onlyDigits(e.target.value).slice(0, 10))}
                inputMode="numeric"
                disabled={disabled}
              />
            </div>
          </div>
        </section>

        <section className={CARD}>
          <div className={H2}>КОНТАКТИ</div>
          <div className="mt-4 space-y-4">
            <div>
              <div className={LBL}>Телефон</div>
              <input
                className={INP}
                type="text"
                inputMode="numeric"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                disabled={disabled}
              />

            </div>

            <div>
              <div className={LBL}>Трето лице</div>
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                  <input
                    type="radio"
                    name="thirdPerson"
                    checked={effectiveThirdPerson === "yes"}
                    onChange={() => setThirdPerson("yes")}
                    disabled={disabled}
                  />
                  Да
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                  <input
                    type="radio"
                    name="thirdPerson"
                    checked={effectiveThirdPerson === "no"}
                    onChange={() => setThirdPerson("no")}
                    disabled={disabled || isThirdPersonScenario}
                  />
                  Не
                </label>
              </div>
            </div>
            {effectiveThirdPerson === "yes" && (
              <div className="space-y-4">
                <div>
                  <div className={LBL}>Телефон (трето лице)</div>
                  <input
                    className={INP}
                    type="text"
                    inputMode="numeric"
                    value={thirdPersonPhone}
                    onChange={(e) => setThirdPersonPhone(e.target.value.replace(/\D/g, ""))}
                    placeholder="+359"
                    disabled={disabled}
                  />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className={LBL}>Име (трето лице)</div>
                    <input
                      className={INP}
                      value={thirdPersonFirstName}
                      onChange={(e) => setThirdPersonFirstName(e.target.value)}
                      disabled={disabled}
                    />
                  </div>

                  <div>
                    <div className={LBL}>Фамилия (трето лице)</div>
                    <input
                      className={INP}
                      value={thirdPersonLastName}
                      onChange={(e) => setThirdPersonLastName(e.target.value)}
                      disabled={disabled}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>
        </section>

        <section className={CARD}>
          <div className={H2}>ЗАДЪЛЖЕНИЯ</div>
          {!!creditorsErr && (
            <div className="mt-3 text-xs text-rose-300">
              {creditorsErr}. Използва се резервният списък.
            </div>
          )}


          <div className="mt-4 space-y-3">
            {debts.map((d, i) => (
              <div key={d._key || i} className="rounded-xl border border-[#1f2638] bg-[#121825] p-3">
                <div className="flex items-center gap-3">
                  <label className="inline-flex items-center gap-2 text-xs text-slate-300">
                    <input
                      type="checkbox"
                      checked={!!d.forCall}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setDebts((prev) => prev.map((x, idx) => (
                          idx === i ? { ...x, forCall: checked } : x
                        )));
                      }}
                      disabled={disabled}
                    />
                    За обаждане
                  </label>

                  <div className="flex-1" />
                  {d.paid && (
                    <span className="px-2 py-0.5 rounded-full text-[11px] bg-emerald-900/30 border border-emerald-700/40 text-emerald-200">
                      Платено
                    </span>
                  )}

                  <button
                    className="btn btn-ghost text-rose-300"
                    onClick={() => {
                      if (debts.length <= 1) return;
                      setDebts((prev) => {
                        const next = prev.filter((_, idx) => idx !== i);
                        if (next.some((item) => item?.forCall)) return next;
                        return next.map((item, idx) => (
                          idx === 0 ? { ...item, forCall: true } : item
                        ));
                      });
                    }}
                    disabled={disabled || debts.length <= 1}
                    title={debts.length <= 1 ? "Трябва да има поне едно задължение" : "Изтрий"}
                    type="button"
                  >
                    Изтрий
                  </button>
                </div>

                <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="sm:col-span-2">
                    <div className={LBL}>Кредитор</div>
                    <select
                      className={INP}
                      value={d.creditor || ""}
                      onChange={(e) => {
                        const v = e.target.value;
                        setDebts(prev => prev.map((x, idx) => idx === i ? { ...x, creditor: v } : x));
                      }}
                      disabled={disabled || (loadingCreditors && !creditorOptions.length)}
                    >
                      {!d.creditor && <option value="">Избери кредитор</option>}
                      {!creditorOptions.length ? (
                        <option value="">
                          {loadingCreditors ? "Зареждане..." : "Няма кредитори"}
                        </option>
                      ) : (
                        creditorOptions.map((creditor) => (
                          <option key={creditor} value={creditor}>{creditor}</option>
                        ))
                      )}
                    </select>
                  </div>

                  <div>
                    <div className={LBL}>Сума (евро)</div>
                    <input
                      className={INP}
                      value={d.amount || ""}
                      onChange={(e) => {
                        const v = normalizeMoneyInput(e.target.value);
                        setDebts(prev => prev.map((x, idx) => idx === i ? { ...x, amount: v } : x));
                      }}
                      onBlur={() => {
                        setDebts(prev => prev.map((x, idx) => {
                          if (idx !== i) return x;
                          return { ...x, amount: formatMoneyOnBlur(x.amount) };
                        }));
                      }}
                      inputMode="decimal"
                      disabled={disabled}
                    />
                  </div>


                  <div className="flex items-end">
                    <div className="text-sm text-slate-400">€</div>
                  </div>
                </div>

                {showInstallmentField && (
                  <div className={`mt-3 grid grid-cols-1 gap-3 ${showDueDateField ? "sm:grid-cols-2" : ""}`}>
                    <div>
                      <div className={LBL}>Вноска (евро)</div>
                      <input
                        className={INP}
                        value={d.installment || ""}
                        onChange={(e) => {
                          const v = normalizeMoneyInput(e.target.value);
                          setDebts((prev) => prev.map((x, idx) => idx === i ? { ...x, installment: v } : x));
                        }}
                        onBlur={() => {
                          setDebts((prev) => prev.map((x, idx) => {
                            if (idx !== i) return x;
                            return { ...x, installment: formatMoneyOnBlur(x.installment) };
                          }));
                        }}
                        inputMode="decimal"
                        disabled={disabled}
                      />
                    </div>
                    {showDueDateField && (
                      <div>
                        <div className={LBL}>Падежна дата</div>
                        <input
                          type="date"
                          className={INP}
                          value={d.dueDate || ""}
                          onChange={(e) => {
                            const v = normalizeDateInput(e.target.value);
                            setDebts((prev) => prev.map((x, idx) => idx === i ? { ...x, dueDate: v } : x));
                          }}
                          disabled={disabled}
                        />
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}

            <button
              className={BTN_PRIMARY}
              type="button"
              onClick={() => {
                setDebts(prev => [...prev, createDebtEntry(defaultCreditorName, false)]);

              }}
              disabled={disabled}
            >
              + Ново задължение
            </button>
          </div>
        </section>

        <section className={CARD}>
          <div className={H2}>ПЛАТЕНИ ЗАДЪЛЖЕНИЯ</div>
          <div className="mt-4 rounded-xl border border-[#1f2638] overflow-hidden">
            <div className="grid grid-cols-1 sm:grid-cols-[1fr_160px_130px] bg-[#0e1320]/60 px-3 py-2 text-[11px] uppercase tracking-wider text-slate-400">
              <div>Кредитор</div>
              <div className="sm:text-right">Сума</div>
              <div className="sm:text-center">Платено</div>
            </div>

            {debts.length ? (
              debts.map((d, i) => (
                <div
                  key={d._key || i}
                  className="grid grid-cols-1 sm:grid-cols-[1fr_160px_130px] items-center gap-2 sm:gap-0 px-3 py-2 border-t border-[#1f2638] bg-[#121825]"
                >
                  <div className="text-sm text-slate-200">{d.creditor || "—"}</div>

                  <div className="text-sm text-slate-200 sm:text-right tabular-nums">
                    {d.amount ? `${d.amount} €` : "—"}
                  </div>

                  <label className="inline-flex items-center justify-start sm:justify-center gap-2 text-sm text-slate-200">
                    <input
                      type="checkbox"
                      checked={!!d.paid}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setDebts((prev) => prev.map((x, idx) => (idx === i ? { ...x, paid: checked } : x)));
                      }}
                      disabled={disabled}
                    />
                    <span className={d.paid ? "text-emerald-300" : "text-slate-400"}>
                      {d.paid ? "Да" : "Не"}
                    </span>
                  </label>
                </div>
              ))
            ) : (
              <div className="px-3 py-3 text-sm text-slate-400">Няма задължения.</div>
            )}
          </div>
        </section>

        <section className={CARD}>
          <div className="flex items-center gap-3">
            <div className={H2}>ИСТОРИЯ НА КОНТАКТИТЕ</div>
            <div className="flex-1" />
            <button
              type="button"
              className={BTN}
              onClick={() => setHistoryEntries((prev) => [...prev, createHistoryEntry()])}
              disabled={disabled}
            >
              Нов запис
            </button>
          </div>

          <div className="mt-4 space-y-3">
            {historyEntries.map((entry, i) => (
              <div
                key={entry._key || i}
                className="rounded-xl border border-[#1f2638] bg-[#121825] p-3"
              >
                {historyEntries.length > 1 && (
                  <div className="mb-3 flex justify-end">
                    <button
                      type="button"
                      className={BTN_GHOST}
                      onClick={() =>
                        setHistoryEntries((prev) => prev.filter((_, idx) => idx !== i))
                      }
                      disabled={disabled}
                    >
                      Премахни
                    </button>
                  </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <div className={LBL}>Дата на разговор</div>
                    <input
                      type="date"
                      className={INP}
                      value={entry.lastCallDate}
                      onChange={(e) => {
                        const value = e.target.value;
                        setHistoryEntries((prev) =>
                          prev.map((item, idx) =>
                            idx === i ? { ...item, lastCallDate: value } : item
                          )
                        );
                      }}
                      disabled={disabled}
                    />
                  </div>
                  <div>
                    <div className={LBL}>Резултат от разговор</div>
                    <select
                      className={INP}
                      value={entry.lastCallResult}
                      onChange={(e) => {
                        const value = e.target.value;
                        setHistoryEntries((prev) =>
                          prev.map((item, idx) =>
                            idx === i ? { ...item, lastCallResult: value } : item
                          )
                        );
                      }}
                      disabled={disabled}
                    >
                      <option value="">Избери</option>
                      <option value="promise_to_pay">Обещание за плащане</option>
                      <option value="refuse_to_pay">Отказ от плащане</option>
                      <option value="no_answer">Няма връзка</option>
                      <option value="wrong_number">Грешен номер</option>
                      <option value="third_person">Трето лице</option>
                    </select>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className={CARD}>
          <div className={H2}>РАБОТНА ИНФОРМАЦИЯ</div>
          <div className="mt-4 space-y-4">
            <div>
              <div className={LBL}>Работи ли в момента</div>
              <div className="mt-2 flex flex-wrap gap-4">
                <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                  <input type="radio" name="isWorking" checked={isWorking === "yes"} onChange={() => setIsWorking("yes")} disabled={disabled} />
                  Да
                </label>
                <label className="inline-flex items-center gap-2 text-sm text-slate-200">
                  <input type="radio" name="isWorking" checked={isWorking === "no"} onChange={() => setIsWorking("no")} disabled={disabled} />
                  Не
                </label>
              </div>
            </div>

            <div>
              <div className={LBL}>Размер на заплата</div>
              <div className="grid grid-cols-[1fr_auto] gap-3 items-center">
                <input
                  className={INP}
                  value={salary}
                  onChange={(e) => setSalary(onlyDigits(e.target.value))}
                  inputMode="numeric"
                  disabled={disabled || isWorking !== "yes"}
                />
                <div className="text-sm text-slate-400">€</div>
              </div>
            </div>
          </div>
        </section>

        <section className={[CARD, "lg:col-span-2"].join(" ")}>
          <div className={H2}>БЕЛЕЖКИ</div>
          <div className="mt-4">
            <textarea
              className={[INP, "min-h-[120px] resize-y"].join(" ")}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              disabled={disabled}
            />
          </div>
        </section>
      </div>

      <div className="flex items-center justify-center">
        <button className={BTN_PRIMARY} onClick={save} disabled={disabled}>
          {saving ? "Запис..." : "Запази"}
        </button>
      </div>

      {toast && (
        <div className="fixed top-6 left-1/2 -translate-x-1/2 z-[60]">
          <div className="rounded-xl border border-slate-800/60 bg-[#0f1523] px-5 py-3 shadow-lg text-sm text-slate-200">
            {toast}
          </div>
        </div>
      )}
    </div>
  );
}
